import { useFormik } from "formik";
import * as Yup from "yup";
import { useState, useCallback, useEffect, useRef } from "react";

export const useFormSchedule = (onSubmit, afterSubmit, initialData, isEditMode) => {
  const [actividades, setActividades] = useState([]);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const isMounted = useRef(true);

  // Inicializar actividades solo una vez en modo edición
  useEffect(() => {
    if (isEditMode && initialData?.actividades) {
      setActividades(initialData.actividades);
    }

    return () => {
      isMounted.current = false;
    };
  }, [isEditMode]);

  const validationSchema = Yup.object().shape({
    fechaHora: Yup.string().required("La fecha y hora es obligatoria"),
    technicalId: Yup.number()
      .typeError("Debe seleccionar un técnico")
      .required("Debe seleccionar un técnico"),
    commerceId: Yup.number()
      .typeError("Debe seleccionar un comercio")
      .required("Debe seleccionar un comercio"),
    zoneId: Yup.number()
      .typeError("Debe seleccionar una zona")
      .required("Debe seleccionar una zona"),
    deviceId: Yup.number()
      .typeError("Debe seleccionar un equipo")
      .required("Debe seleccionar un equipo"),
    activityTypeId: Yup.number()
      .typeError("Debe seleccionar un tipo de actividad")
      .required("Debe seleccionar un tipo de actividad"),
    duracion: Yup.string().nullable(),
    description: Yup.string().nullable(),
  });

  const formik = useFormik({
    initialValues: {
      fechaHora: initialData?.fechaHora || "",
      technicalId: initialData?.technicalId || "",
      commerceId: initialData?.commerceId || "",
      zoneId: initialData?.zoneId || "",
      deviceId: initialData?.deviceId || "",
      activityTypeId: initialData?.activityTypeId || "",
      duracion: "",
      description: "",
    },
    validationSchema,
    enableReinitialize: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setSubmitError(null);
        setSubmitting(true);

        if (actividades.length === 0) {
          setSubmitError("Debe agregar al menos una actividad");
          setSubmitting(false);
          return;
        }

        const payload = {
          ...values,
          actividades,
        };

        await onSubmit(payload);

        if (isMounted.current) {
          setSubmitSuccess(true);
          afterSubmit?.();

          if (!isEditMode) {
            resetForm();
            setActividades([]);
          }
        }
      } catch (error) {
        if (isMounted.current) {
          console.error("Error en submit:", error);
          setSubmitError(
            error.response?.data?.message || error.message || "Error al guardar el agendamiento"
          );
        }
      } finally {
        if (isMounted.current) {
          setSubmitting(false);
        }
      }
    },
  });

  // Actualizar valores manualmente cuando initialData cambie en modo edición
  useEffect(() => {
    if (isEditMode && initialData && isMounted.current) {
      const newValues = {
        fechaHora: initialData.fechaHora || "",
        technicalId: initialData.technicalId || "",
        commerceId: initialData.commerceId || "",
        zoneId: initialData.zoneId || "",
        deviceId: initialData.deviceId || "",
        activityTypeId: initialData.activityTypeId || "",
        duracion: "",
        description: "",
      };

      // Usar setTimeout para evitar actualizaciones sincrónicas
      setTimeout(() => {
        if (isMounted.current) {
          formik.setValues(newValues);
        }
      }, 0);
    }
  }, [isEditMode, initialData]);

  const agregarActividad = useCallback(
    (zones = [], devices = [], activityTypes = []) => {
      const { duracion, zoneId, deviceId, activityTypeId, description } = formik.values;

      // Validar campos requeridos
      const errors = {};
      if (!duracion) errors.duracion = "La duración es requerida";
      if (!zoneId) errors.zoneId = "La zona es requerida";
      if (!deviceId) errors.deviceId = "El equipo es requerido";
      if (!activityTypeId) errors.activityTypeId = "El tipo de actividad es requerido";
      if (!description) errors.description = "La descripción es requerida";

      if (Object.keys(errors).length > 0) {
        Object.entries(errors).forEach(([field, message]) => {
          formik.setFieldError(field, message);
        });
        setSubmitError("Por favor complete todos los campos de la actividad.");
        return;
      }

      // Buscar los nombres correspondientes a los IDs
      const zonaSeleccionada = zones.find((z) => Number(z.id) === Number(zoneId));
      const equipoSeleccionado = devices.find((d) => Number(d.id) === Number(deviceId));
      const tipoActividad = activityTypes.find((a) => Number(a.id) === Number(activityTypeId));

      const nuevaActividad = {
        duracion,
        zoneId,
        zone: zonaSeleccionada?.title || "",
        deviceId,
        device: equipoSeleccionado?.nomenclature || "",
        activityTypeId,
        activity: tipoActividad?.title || "",
        description,
      };

      setActividades((prev) => [...prev, nuevaActividad]);

      // Limpiar campos de actividad de forma segura
      setTimeout(() => {
        if (isMounted.current) {
          formik.setValues((prevValues) => ({
            ...prevValues,
            duracion: "",
            zoneId: "",
            deviceId: "",
            activityTypeId: "",
            description: "",
          }));

          formik.setErrors((prevErrors) => ({
            ...prevErrors,
            duracion: undefined,
            zoneId: undefined,
            deviceId: undefined,
            activityTypeId: undefined,
            description: undefined,
          }));
        }
      }, 0);

      setSubmitError(null);
    },
    [formik]
  );

  const eliminarActividad = useCallback((index) => {
    setActividades((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const resetForm = useCallback(() => {
    if (isMounted.current) {
      formik.resetForm();
      setActividades([]);
      setSubmitError(null);
      setSubmitSuccess(false);
    }
  }, [formik]);

  return {
    ...formik,
    actividades,
    agregarActividad,
    eliminarActividad,
    submitError,
    submitSuccess,
    setSubmitError,
    setSubmitSuccess,
    resetForm,
  };
};
